import React, { useCallback, useEffect, useState } from 'react';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { getApiErrorMessage } from '../../utils/errors';
import { effortConfigSaveError, lineSaveError, resolveLineFamilyId } from './formUtils';
import {
  createLlmFamily,
  createLlmModelLine,
  listLlmEffortConfigs,
  listLlmEffortLevels,
  listLlmFamilies,
  listLlmModelLines,
  updateLlmFamily,
  updateLlmModelLine,
  upsertLlmEffortConfig,
} from '../../api/llmCatalogAdmin';

const PROVIDER_OPTIONS = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
];

function StatusBadge({ active }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${
        active
          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
          : 'bg-slate-100 text-slate-600 border border-slate-200'
      }`}
    >
      {active ? 'Ativo' : 'Inativo'}
    </span>
  );
}

function LlmCatalogAdmin() {
  const [families, setFamilies] = useState([]);
  const [modelLines, setModelLines] = useState([]);
  const [efforts, setEfforts] = useState([]);
  const [effortConfigs, setEffortConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const [familyForm, setFamilyForm] = useState({
    id: '',
    slug: '',
    label: '',
    provider: 'openai',
    apiKey: '',
    sortOrder: '0',
    isActive: false,
  });

  const [lineForm, setLineForm] = useState({
    id: '',
    familyId: '',
    modelNumber: '1',
    internalModel: '',
    isActive: true,
  });

  const [configForm, setConfigForm] = useState({
    effortId: '',
    provider: 'openai',
    llmKwargs: '{}',
  });

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [familiesRes, linesRes, effortsRes, configsRes] = await Promise.all([
        listLlmFamilies(),
        listLlmModelLines(),
        listLlmEffortLevels(),
        listLlmEffortConfigs(),
      ]);
      setFamilies(familiesRes.data || []);
      setModelLines(linesRes.data || []);
      setEfforts(effortsRes.data || []);
      setEffortConfigs(configsRes.data || []);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar o catálogo LLM.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const resetFamilyForm = () => {
    setFamilyForm({
      id: '',
      slug: '',
      label: '',
      provider: 'openai',
      apiKey: '',
      sortOrder: '0',
      isActive: false,
    });
  };

  const resetLineForm = () => {
    setLineForm({
      id: '',
      familyId: families[0]?.id || '',
      modelNumber: '1',
      internalModel: '',
      isActive: true,
    });
  };

  const handleEditFamily = (family) => {
    setFamilyForm({
      id: family.id,
      slug: family.slug,
      label: family.label,
      provider: family.provider,
      apiKey: '',
      sortOrder: String(family.sort_order ?? 0),
      isActive: family.is_active,
    });
  };

  const handleEditLine = (line) => {
    setLineForm({
      id: line.id,
      familyId: line.family_id,
      modelNumber: String(line.model_number),
      internalModel: line.internal_model,
      isActive: line.is_active,
    });
  };

  const handleSaveFamily = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    const payload = {
      label: familyForm.label.trim(),
      provider: familyForm.provider,
      sort_order: parseInt(familyForm.sortOrder, 10) || 0,
      is_active: familyForm.isActive,
    };
    if (familyForm.apiKey.trim()) {
      payload.api_key = familyForm.apiKey.trim();
    }

    try {
      if (familyForm.id) {
        await updateLlmFamily(familyForm.id, payload);
        setSuccess('Família atualizada.');
      } else {
        await createLlmFamily({
          slug: familyForm.slug.trim().toLowerCase(),
          ...payload,
          api_key: familyForm.apiKey.trim() || undefined,
        });
        setSuccess('Família criada.');
        resetFamilyForm();
      }
      await loadCatalog();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar a família.'));
    } finally {
      setSaving(false);
    }
  };

  const selectedFamilyId = resolveLineFamilyId(lineForm.familyId, families, {
    editing: Boolean(lineForm.id),
  });

  const handleSaveLine = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!lineForm.id) {
      const familyError = lineSaveError(selectedFamilyId);
      if (familyError) {
        setError(familyError);
        return;
      }
    }

    setSaving(true);

    const payload = {
      family_id: selectedFamilyId,
      model_number: parseInt(lineForm.modelNumber, 10),
      internal_model: lineForm.internalModel.trim(),
      is_active: lineForm.isActive,
    };

    try {
      if (lineForm.id) {
        await updateLlmModelLine(lineForm.id, {
          model_number: payload.model_number,
          internal_model: payload.internal_model,
          is_active: payload.is_active,
        });
        setSuccess('Linha de modelo atualizada.');
      } else {
        await createLlmModelLine(payload);
        setSuccess('Linha de modelo criada.');
        resetLineForm();
      }
      await loadCatalog();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar a linha de modelo.'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveConfig = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    const effortError = effortConfigSaveError(configForm.effortId);
    if (effortError) {
      setError(effortError);
      return;
    }

    setSaving(true);

    let llmKwargs;
    try {
      llmKwargs = JSON.parse(configForm.llmKwargs || '{}');
    } catch {
      setError('llm_kwargs deve ser um JSON válido.');
      setSaving(false);
      return;
    }

    try {
      await upsertLlmEffortConfig({
        effort_id: configForm.effortId,
        provider: configForm.provider,
        llm_kwargs: llmKwargs,
      });
      setSuccess('Configuração de esforço salva.');
      await loadCatalog();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar a configuração.'));
    } finally {
      setSaving(false);
    }
  };

  const familyOptions = families.map((family) => ({
    value: family.id,
    label: family.label,
  }));

  const effortOptions = efforts.map((effort) => ({
    value: effort.id,
    label: effort.label,
  }));

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-content-primary">Catálogo LLM</h1>
        <p className="text-content-muted text-sm mt-1">
          Gerencie famílias, linhas de modelo, esforços e credenciais criptografadas.
        </p>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
          <p className="text-sm text-emerald-700">{success}</p>
        </div>
      )}

      {loading ? (
        <div className="surface-card px-4 py-12 text-center">
          <p className="text-content-muted text-sm">Carregando catálogo...</p>
        </div>
      ) : (
        <>
          <section className="surface-card p-5 space-y-4">
            <h2 className="text-lg font-semibold text-content-primary">Famílias</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-surface-border text-left">
                    <th className="py-2 pr-4">Slug</th>
                    <th className="py-2 pr-4">Label</th>
                    <th className="py-2 pr-4">Provider</th>
                    <th className="py-2 pr-4">Key</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {families.map((family) => (
                    <tr key={family.id} className="border-b border-surface-border last:border-b-0">
                      <td className="py-2 pr-4 font-mono text-xs">{family.slug}</td>
                      <td className="py-2 pr-4">{family.label}</td>
                      <td className="py-2 pr-4 capitalize">{family.provider}</td>
                      <td className="py-2 pr-4">
                        {family.api_key_configured ? 'Configurada' : 'Pendente'}
                      </td>
                      <td className="py-2 pr-4">
                        <StatusBadge active={family.is_active} />
                      </td>
                      <td className="py-2">
                        <button
                          type="button"
                          className="text-xs text-forge-600 hover:text-forge-700"
                          onClick={() => handleEditFamily(family)}
                        >
                          Editar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <form onSubmit={handleSaveFamily} className="grid gap-4 md:grid-cols-2 pt-4 border-t border-surface-border">
              {!familyForm.id && (
                <Input
                  id="family_slug"
                  label="Slug"
                  value={familyForm.slug}
                  onChange={(e) => setFamilyForm((current) => ({ ...current, slug: e.target.value }))}
                  placeholder="pulse"
                  required
                />
              )}
              <Input
                id="family_label"
                label="Label"
                value={familyForm.label}
                onChange={(e) => setFamilyForm((current) => ({ ...current, label: e.target.value }))}
                placeholder="Pulse"
                required
              />
              <Select
                id="family_provider"
                label="Provider"
                value={familyForm.provider}
                onChange={(e) => setFamilyForm((current) => ({ ...current, provider: e.target.value }))}
                options={PROVIDER_OPTIONS}
              />
              <Input
                id="family_api_key"
                label="API key"
                type="password"
                value={familyForm.apiKey}
                onChange={(e) => setFamilyForm((current) => ({ ...current, apiKey: e.target.value }))}
                placeholder={familyForm.id ? 'Deixe em branco para manter' : 'sk-...'}
                hint="Armazenada criptografada no banco. Nunca é exibida após salvar."
              />
              <Input
                id="family_sort_order"
                label="Ordem"
                type="number"
                value={familyForm.sortOrder}
                onChange={(e) => setFamilyForm((current) => ({ ...current, sortOrder: e.target.value }))}
              />
              <label className="flex items-center gap-2 text-sm text-content-secondary md:col-span-2">
                <input
                  type="checkbox"
                  checked={familyForm.isActive}
                  onChange={(e) => setFamilyForm((current) => ({ ...current, isActive: e.target.checked }))}
                />
                Família ativa (requer API key configurada)
              </label>
              <div className="flex gap-2 md:col-span-2">
                <button type="submit" className="btn-primary text-sm px-4 py-2" disabled={saving}>
                  {familyForm.id ? 'Atualizar família' : 'Criar família'}
                </button>
                {familyForm.id && (
                  <button type="button" className="btn-secondary text-sm px-4 py-2" onClick={resetFamilyForm}>
                    Cancelar edição
                  </button>
                )}
              </div>
            </form>
          </section>

          <section className="surface-card p-5 space-y-4">
            <h2 className="text-lg font-semibold text-content-primary">Linhas de modelo</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-surface-border text-left">
                    <th className="py-2 pr-4">Público</th>
                    <th className="py-2 pr-4">Interno</th>
                    <th className="py-2 pr-4">Família</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {modelLines.map((line) => (
                    <tr key={line.id} className="border-b border-surface-border last:border-b-0">
                      <td className="py-2 pr-4">{line.public_label}</td>
                      <td className="py-2 pr-4 font-mono text-xs">{line.internal_model}</td>
                      <td className="py-2 pr-4">{line.family_label}</td>
                      <td className="py-2 pr-4">
                        <StatusBadge active={line.is_active} />
                      </td>
                      <td className="py-2">
                        <button
                          type="button"
                          className="text-xs text-forge-600 hover:text-forge-700"
                          onClick={() => handleEditLine(line)}
                        >
                          Editar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <form onSubmit={handleSaveLine} className="grid gap-4 md:grid-cols-2 pt-4 border-t border-surface-border">
              <Select
                id="line_family"
                label="Família"
                value={selectedFamilyId}
                onChange={(e) => setLineForm((current) => ({ ...current, familyId: e.target.value }))}
                options={familyOptions}
                disabled={Boolean(lineForm.id)}
              />
              <Input
                id="line_model_number"
                label="Número"
                type="number"
                value={lineForm.modelNumber}
                onChange={(e) => setLineForm((current) => ({ ...current, modelNumber: e.target.value }))}
                required
              />
              <Input
                id="line_internal_model"
                label="Modelo interno"
                value={lineForm.internalModel}
                onChange={(e) => setLineForm((current) => ({ ...current, internalModel: e.target.value }))}
                placeholder="gpt-4o"
                required
              />
              <label className="flex items-center gap-2 text-sm text-content-secondary">
                <input
                  type="checkbox"
                  checked={lineForm.isActive}
                  onChange={(e) => setLineForm((current) => ({ ...current, isActive: e.target.checked }))}
                />
                Linha ativa
              </label>
              <div className="flex gap-2 md:col-span-2">
                <button type="submit" className="btn-primary text-sm px-4 py-2" disabled={saving}>
                  {lineForm.id ? 'Atualizar linha' : 'Criar linha'}
                </button>
                {lineForm.id && (
                  <button type="button" className="btn-secondary text-sm px-4 py-2" onClick={resetLineForm}>
                    Cancelar edição
                  </button>
                )}
              </div>
            </form>
          </section>

          <section className="surface-card p-5 space-y-4">
            <h2 className="text-lg font-semibold text-content-primary">Esforços</h2>
            <ul className="text-sm space-y-2">
              {efforts.map((effort) => (
                <li key={effort.id} className="flex items-center justify-between gap-3">
                  <span>
                    {effort.label} <span className="text-content-muted">({effort.slug})</span>
                  </span>
                  <StatusBadge active={effort.is_active} />
                </li>
              ))}
            </ul>
          </section>

          <section className="surface-card p-5 space-y-4">
            <h2 className="text-lg font-semibold text-content-primary">Configs de esforço</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-surface-border text-left">
                    <th className="py-2 pr-4">Esforço</th>
                    <th className="py-2 pr-4">Provider</th>
                    <th className="py-2">llm_kwargs</th>
                  </tr>
                </thead>
                <tbody>
                  {effortConfigs.map((config) => {
                    const effort = efforts.find((item) => item.id === config.effort_id);
                    return (
                      <tr key={config.id} className="border-b border-surface-border last:border-b-0">
                        <td className="py-2 pr-4">{effort?.label || config.effort_id}</td>
                        <td className="py-2 pr-4 capitalize">{config.provider}</td>
                        <td className="py-2 font-mono text-xs break-all">
                          {JSON.stringify(config.llm_kwargs || {})}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <form onSubmit={handleSaveConfig} className="grid gap-4 md:grid-cols-2 pt-4 border-t border-surface-border">
              <Select
                id="config_effort"
                label="Esforço"
                value={configForm.effortId}
                onChange={(e) => setConfigForm((current) => ({ ...current, effortId: e.target.value }))}
                options={[{ value: '', label: 'Selecione...' }, ...effortOptions]}
              />
              <Select
                id="config_provider"
                label="Provider"
                value={configForm.provider}
                onChange={(e) => setConfigForm((current) => ({ ...current, provider: e.target.value }))}
                options={PROVIDER_OPTIONS}
              />
              <div className="md:col-span-2">
                <label htmlFor="config_llm_kwargs" className="block text-sm font-medium text-slate-700 mb-2">
                  llm_kwargs (JSON)
                </label>
                <textarea
                  id="config_llm_kwargs"
                  value={configForm.llmKwargs}
                  onChange={(e) => setConfigForm((current) => ({ ...current, llmKwargs: e.target.value }))}
                  rows={4}
                  className="field-input font-mono text-xs"
                />
              </div>
              <div className="md:col-span-2">
                <button type="submit" className="btn-primary text-sm px-4 py-2" disabled={saving}>
                  Salvar config
                </button>
              </div>
            </form>
          </section>
        </>
      )}
    </div>
  );
}

export default LlmCatalogAdmin;
